import React, { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { Eye, EyeOff, ShieldCheck, Lock, RefreshCw } from 'lucide-react'
import { authService } from '@/services/auth'
import { usePortal } from '@/contexts/PortalContext'
import { useRecaptchaSiteKey } from '@/hooks/useRecaptchaSiteKey'
import { useAuthStore } from '@/stores/authStore'
import { postLoginDestination } from '@/utils/postLoginDestination'
import { getErrorMessage } from '@/services/api'
import { readAuthReturn } from '@/utils/authReturn'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Card } from '@/components/ui/Card'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

const REMEMBER_ME_KEY = 'silux_remember_me'
const SAVED_EMAIL_KEY = 'silux_saved_email'
// SECURITY: we never persist the password. This key only exists to purge any
// plaintext password previously stored by older builds.
const LEGACY_SAVED_PASSWORD_KEY = 'silux_saved_password'

export const Login: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login: storeLogin } = useAuthStore()
  const { portal } = usePortal()
  // reCAPTCHA site key selection.
  // - Main site: use the build-time env key (registered for siluxmobile.co.uk).
  // - White-label portal (*.mobileflow.uk): the main-site key is domain-locked, so
  //   Google renders an "Invalid domain" box that NEVER fires onChange — which would
  //   leave the Sign In button permanently disabled. So on a portal we ONLY use a
  //   portal-specific key (admin Settings → Security → RECAPTCHA_PORTAL_SITE_KEY).
  //   If none is configured, we skip reCAPTCHA entirely rather than trap the user.
  //   (The backend login already fails open when no/invalid token is supplied.)
  const recaptchaSiteKey = useRecaptchaSiteKey()
  const recaptchaEnabled = !!recaptchaSiteKey

  // Show banner if redirected here due to inactivity timeout
  const timedOut = searchParams.get('reason') === 'timeout'

  // Where to send a customer after login — e.g. back to checkout (cart preserved) instead of the
  // account page. Carried in sessionStorage (set by the checkout gate, TTL-stamped) so it survives
  // the OTP step and URL changes; falls back to a ?returnTo= param. Internal same-origin paths only.
  // READ ONLY here — never clear it. GuestRoute reads the same value via readAuthReturn(), so
  // whichever redirect wins the race lands on the same place; the destination page clears it on
  // arrival. Clearing here would let the guard read empty and bounce to the dashboard (the bug we
  // hit). A stale/abandoned flag is auto-ignored by readAuthReturn()'s TTL.
  const paramReturnTo = searchParams.get('returnTo')
  const returnTo = (paramReturnTo && paramReturnTo.startsWith('/') && !paramReturnTo.startsWith('//')
    ? paramReturnTo
    : null) || readAuthReturn()

  // ── Step 1 (email + password) state ──────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  // ── Step 2 (OTP) state ────────────────────────────────────────────────────
  const [showOTP, setShowOTP] = useState(false)
  const [otpToken, setOtpToken] = useState('')
  const [otpHint, setOtpHint] = useState('')
  const [otpVia, setOtpVia] = useState<'sms' | 'email'>('sms')
  const [isFirstLogin, setIsFirstLogin] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [otpLoading, setOtpLoading] = useState(false)
  const [retriesRemaining, setRetriesRemaining] = useState(3)
  const [resendsRemaining, setResendsRemaining] = useState(3)
  const [resendLoading, setResendLoading] = useState(false)
  const [locked, setLocked] = useState(false)
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null)
  const [lockedMinsRemaining, setLockedMinsRemaining] = useState(0)

  // Countdown timer for lock
  useEffect(() => {
    if (!locked || !lockedUntil) return
    const tick = () => {
      const diff = Math.max(0, Math.ceil((lockedUntil.getTime() - Date.now()) / 60000))
      setLockedMinsRemaining(diff)
      if (diff <= 0) setLocked(false)
    }
    tick()
    const id = setInterval(tick, 15000)
    return () => clearInterval(id)
  }, [locked, lockedUntil])

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) })

  // Load saved email on mount (never the password — see below)
  useEffect(() => {
    // Purge any plaintext password persisted by an older build.
    localStorage.removeItem(LEGACY_SAVED_PASSWORD_KEY)
    const savedRememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true'
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY)
    if (savedRememberMe && savedEmail) {
      setRememberMe(true)
      setValue('email', savedEmail)
    }
  }, [setValue])

  // ── Step 1: submit email + password ──────────────────────────────────────
  const onSubmit = async (data: LoginFormData) => {
    if (recaptchaEnabled && !recaptchaToken) {
      setError('Please complete the security check before signing in.')
      return
    }
    setIsLoading(true)
    setError(null)

    try {
      const recaptchaTokenToSend = recaptchaToken
      if (recaptchaEnabled) {
        recaptchaRef.current?.reset()
        setRecaptchaToken(null)
      }

      const response = await authService.login({ ...data, recaptcha_token: recaptchaTokenToSend || undefined })

      // Handle Remember Me — persist the email only, NEVER the password.
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true')
        localStorage.setItem(SAVED_EMAIL_KEY, data.email)
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY)
        localStorage.removeItem(SAVED_EMAIL_KEY)
      }
      localStorage.removeItem(LEGACY_SAVED_PASSWORD_KEY)

      // Backend may return tokens directly (OTP disabled/bypassed) or an OTP challenge
      if ('access_token' in response && response.access_token && response.user) {
        // OTP bypassed — log in directly
        storeLogin(response.access_token, response.refresh_token, response.user)
        // One shared rule, also used by the GuestRoute guard on this page — which re-renders
        // the instant the store says "authenticated" and would otherwise override this.
        const dest = postLoginDestination(response.user)
        navigate(dest === '/account' ? (returnTo || '/account') : dest)
        return
      }

      // OTP required — show verification form
      const otpResponse = response as import('@/services/auth').LoginOTPPendingResponse
      setOtpToken(otpResponse.otp_token)
      setOtpHint(otpResponse.message)
      setOtpVia((otpResponse as any).otp_via === 'email' ? 'email' : 'sms')
      setIsFirstLogin(!!(otpResponse as any).first_login)
      setResendsRemaining(otpResponse.resends_remaining ?? 3)
      setRetriesRemaining(3)
      setOtpCode('')
      setOtpError(null)
      setLocked(false)
      setShowOTP(true)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  // ── Step 2: verify OTP ────────────────────────────────────────────────────
  const handleVerifyOTP = async () => {
    if (!otpCode.trim()) {
      setOtpError('Please enter the verification code.')
      return
    }
    setOtpLoading(true)
    setOtpError(null)

    try {
      const res = await authService.verifyLoginOTP({ otp_token: otpToken, otp_code: otpCode.trim() })

      // Success — server returned JWT tokens
      if (res.access_token && res.user) {
        storeLogin(res.access_token, res.refresh_token!, res.user)
        const dest = postLoginDestination(res.user)
        if ((res as any).must_complete_profile) {
          navigate('/complete-profile')
        } else if (dest !== '/account') {
          navigate(dest)
        } else if ((res as any).show_spin_wheel) {
          navigate('/spin-wheel')
        } else {
          navigate(returnTo || '/account')
        }
        return
      }

      // Failure path
      if (res.locked) {
        setLocked(true)
        setRetriesRemaining(0)
        if (res.locked_until) setLockedUntil(new Date(res.locked_until))
        setOtpError(res.message || 'Account locked.')
      } else {
        setRetriesRemaining(res.retries_remaining ?? 0)
        setOtpError(res.message || 'Incorrect code.')
      }
    } catch (err) {
      setOtpError(getErrorMessage(err))
    } finally {
      setOtpLoading(false)
    }
  }

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOTP = async () => {
    if (resendsRemaining <= 0) return
    setResendLoading(true)
    setOtpError(null)

    try {
      const res = await authService.resendLoginOTP(otpToken)
      setResendsRemaining(res.resends_remaining)
      setRetriesRemaining(3)
      setOtpCode('')
      setOtpError(null)
    } catch (err) {
      setOtpError(getErrorMessage(err))
    } finally {
      setResendLoading(false)
    }
  }

  // ── Back to login ─────────────────────────────────────────────────────────
  const handleBackToLogin = () => {
    setShowOTP(false)
    setOtpToken('')
    setOtpCode('')
    setOtpError(null)
    setLocked(false)
    setLockedUntil(null)
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl xs:text-3xl font-bold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Or{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700">
              create a new account
            </Link>
          </p>
        </div>

        {/* ── Inactivity timeout banner ── */}
        {timedOut && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
            <span>⏱</span>
            <span>You were automatically logged out due to inactivity. Please sign in again.</span>
          </div>
        )}

        {/* ── Step 1: Email + Password card ── */}
        {!showOTP && (
          <Card className="p-6 sm:p-8">
            {error && (
              <Alert variant="error" message={error} onClose={() => setError(null)} className="mb-4" />
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="Email Address"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={errors.password?.message}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 cursor-pointer"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                  Forgot password?
                </Link>
              </div>

              {recaptchaEnabled && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={setRecaptchaToken}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
              )}

              <Button type="submit" fullWidth isLoading={isLoading} disabled={recaptchaEnabled && !recaptchaToken}>
                Sign In
              </Button>

              {!portal && (
                <div className="pt-3 border-t border-gray-100 text-center text-sm text-gray-600">
                  First time logging in or moved over from our old platform?{' '}
                  <Link to="/first-time-login" className="font-semibold text-amber-600 hover:text-amber-700">
                    Set up your account →
                  </Link>
                </div>
              )}
            </form>
          </Card>
        )}

        {/* ── Step 2: OTP verification popup ── */}
        {showOTP && (
          <Card className="p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 bg-primary-50 rounded-full flex items-center justify-center mb-3">
                <ShieldCheck className="text-primary-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Verify Your Identity</h3>
              <p className="mt-2 text-sm text-gray-600">{otpHint}</p>
            </div>

            {/* Locked state */}
            {locked && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <Lock className="text-red-500 mt-0.5 shrink-0" size={20} />
                <div>
                  <p className="text-sm font-semibold text-red-700">Account Temporarily Locked</p>
                  <p className="text-sm text-red-600 mt-1">
                    Too many incorrect codes. Your account is locked for{' '}
                    <span className="font-bold">{lockedMinsRemaining} minute{lockedMinsRemaining !== 1 ? 's' : ''}</span>.
                    Please try again later or contact support.
                  </p>
                </div>
              </div>
            )}

            {/* OTP error (non-locked) */}
            {otpError && !locked && (
              <Alert variant="error" message={otpError} onClose={() => setOtpError(null)} className="mb-4" />
            )}

            {/* Retries indicator */}
            {!locked && retriesRemaining < 3 && retriesRemaining > 0 && (
              <div className="mb-3 text-center">
                <span className="text-sm text-amber-600 font-medium">
                  {retriesRemaining} attempt{retriesRemaining !== 1 ? 's' : ''} remaining
                </span>
              </div>
            )}

            {/* OTP input */}
            {!locked && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyOTP()}
                    placeholder="Enter 5-digit code"
                    className="w-full px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>

                <Button
                  fullWidth
                  onClick={handleVerifyOTP}
                  isLoading={otpLoading}
                  disabled={otpCode.length < 5}
                >
                  Verify &amp; Sign In
                </Button>
              </div>
            )}

            {/* Resend + Back row */}
            <div className="mt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to login
              </button>

              {!locked && (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendsRemaining <= 0 || resendLoading}
                  className={`flex items-center gap-1 text-sm font-medium ${
                    resendsRemaining > 0
                      ? 'text-primary-600 hover:text-primary-700'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <RefreshCw size={14} className={resendLoading ? 'animate-spin' : ''} />
                  {resendsRemaining > 0
                    ? `Resend code (${resendsRemaining} left)`
                    : 'No resends left'}
                </button>
              )}
            </div>

            {/* Help text */}
            <p className="mt-4 text-center text-xs text-gray-400">
              {otpVia === 'email'
                ? 'The code expires in 5 minutes. Check your email inbox (and spam folder).'
                : 'The code expires in 5 minutes. Check your SMS messages.'}
            </p>
            {isFirstLogin && (
              <p className="mt-2 text-center text-xs text-primary-600">
                After verifying, you will be asked to set a new password and confirm your mobile number.
              </p>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
